import { FC, useEffect, useState } from "react";
import { matchPath, RouteComponentProps, withRouter } from "react-router-dom";
import SelectedMenuSessionVariable from "@app/layout/menus/constants";
import MenuItemEnum from "@app/layout/menus/menuItemEnum";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import AdminMenu from "@app/layout/menus/AdminMenu";
import Dashboards from "@app/layout/menus/Dashboards";
import DataMenu from "@app/layout/menus/DataMenu";
import DownloadsMenu from "@app/layout/menus/DownloadsMenu";
import HelpMenu from "@app/layout/menus/HelpMenu";
import ManageMenu from "@app/layout/menus/ManageMenu";
import OverviewsMenu from "@app/layout/menus/OverviewsMenu";
import ToolsMenu from "@app/layout/menus/ToolsMenu";

//NOTE: As menu items are added the routes should be added here for correct highlighting on initial render.
//This gets the correct page on an initial rendering. After initial render the session is set by click handlers on the menu items.
//This makes the menu highlighting work correctly on initial render for known paths, but will not require constant maintenance when routes are added and changed.
//If a route is left out it will only fail to highlight the menu item if a user navigates to it by manually inputting the URL, rather than through the menu.
const getSelectedItemFromPathname = (pathname: string) => {
  let newMenuItem: MenuItemEnum = MenuItemEnum.Unknown;
  if (!!matchPath(pathname, "/tools/:context") && !matchPath(pathname, "/tools/lookupaddress")
    && !matchPath(pathname, "/tools/accuracyevidence")) {
    newMenuItem = MenuItemEnum.LookupGeography;
  } else if (!!matchPath(pathname, "/tools/lookupaddress")) {
    newMenuItem = MenuItemEnum.LookupAddress;
  } else if (!!matchPath(pathname, "/tutorials/qas")) {
    newMenuItem = MenuItemEnum.DesktopTutorial;
  } else if (!!matchPath(pathname, "/tutorials/qes")) {
    newMenuItem = MenuItemEnum.QESTutorial;
  } else if (!!matchPath(pathname, "/admin/internal-reports")) {
    newMenuItem = MenuItemEnum.InternalReports;
  } else if (!!matchPath(pathname, "/admin")) {
    newMenuItem = MenuItemEnum.Admin;
  } else if (!!matchPath(pathname, "/projects/edit")) {
    newMenuItem = MenuItemEnum.EditProjects;
  } else if (!!matchPath(pathname, { exact: true, path: "/", strict: true }) || !!matchPath(pathname, { path: "/projects" })) {
    newMenuItem = MenuItemEnum.CustomProject;
  } else if (!!matchPath(pathname, "/tools/accuracyevidence")) {
    newMenuItem = MenuItemEnum.AccuracyEvidence;
  } else if (!!matchPath(pathname, "/member-populations")) {
    newMenuItem = MenuItemEnum.Members;
  } else if (!!matchPath(pathname, "/terminations")) {
    newMenuItem = MenuItemEnum.Termination;
  } else if (!!matchPath(pathname, "/tags")) {
    newMenuItem = MenuItemEnum.ManageTags;
  } else if (!!matchPath(pathname, "/admin/users/sessions")) {
    newMenuItem = MenuItemEnum.UserSessions;
  } else if (!!matchPath(pathname, "/access-reports/admin")) {
    newMenuItem = MenuItemEnum.AccessReportsAdmin;
  } else if (!!matchPath(pathname, "/access-reports")) {
    newMenuItem = MenuItemEnum.AccessReports;
  } else if (!!matchPath(pathname, "/reports")) {
    newMenuItem = MenuItemEnum.Reports;
  } else if (!!matchPath(pathname, "/targeting")) {
    newMenuItem = MenuItemEnum.Targeting;
  } else if (!!matchPath(pathname, "/provider-sets")) {
    newMenuItem = MenuItemEnum.Providers;
  } else if (!!matchPath(pathname, "/operations")) {
    newMenuItem = MenuItemEnum.Operations;
  } else if (!!matchPath(pathname, "/performance")) {
    newMenuItem = MenuItemEnum.Performance;
  } else if (!!matchPath(pathname, "/network-outreach")) {
    newMenuItem = MenuItemEnum.NetworkOutreach;
  } else if (!!matchPath(pathname, "/network-submission")) {
    newMenuItem = MenuItemEnum.NetworkSubmission;
  } else if (!!matchPath(pathname, "/overviews/enterprise-compliance-dashboard")) {
    newMenuItem = MenuItemEnum.EnterpriseComplianceDashboard;
  } else if (!!matchPath(pathname, "/overviews/competitive-insights-dashboard")) {
    newMenuItem = MenuItemEnum.CompetitiveInsightsDashboard;
  } else if (!!matchPath(pathname, "/overviews/market-expansion-dashboard")) {
    newMenuItem = MenuItemEnum.MarketExpansionDashboard;
  } else if (!!matchPath(pathname, "/overviews/compliance-connect")) {
    newMenuItem = MenuItemEnum.ComplianceConnect;
  } else if (!!matchPath(pathname, "/exceptions")) {
    newMenuItem = MenuItemEnum.Exceptions;
  } else if (!!matchPath(pathname, "/report-presets")) {
    newMenuItem = MenuItemEnum.ReportPresets;
  }
  return newMenuItem;
};

const Menu: FC<React.PropsWithChildren<RouteComponentProps>> = (props: RouteComponentProps) => {
  // We have to rely on the session storage for the selected item because 1.0 lives outside this router,
  // so this state value is just to force a re-render when both a) the pathname changes and b) the selected item changes.
  // Otherwise, the render happens before the session storage item updates, so the selected menu item doesn't highlight.
  const [, setSelectedItem] = useState<MenuItemEnum>(sessionStorage.getItem(SelectedMenuSessionVariable) as MenuItemEnum || MenuItemEnum.Unknown);

  useEffect(() => {
    const newSelectedItem = getSelectedItemFromPathname(props.location.pathname);
    sessionStorage.setItem(SelectedMenuSessionVariable, newSelectedItem);
    setSelectedItem(newSelectedItem);
  }, [props.location.pathname]);

  return (
    <>
      <RoleRestricted allowedRoles={[Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER, Roles.COMPETITIVE_INSIGHTS_USER, Roles.NEW_MARKET_EXPANSION_USER]}>
        <OverviewsMenu />
      </RoleRestricted>

      <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.QUEST_USER, Roles.CLIENT_ADMIN, Roles.VIEWER]}>
        <Dashboards />
      </RoleRestricted>

      <ManageMenu />

      <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.QUEST_USER, Roles.CLIENT_ADMIN, Roles.VIEWER]}>
        <DataMenu />
      </RoleRestricted>

      <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.QUEST_USER, Roles.CLIENT_ADMIN, Roles.VIEWER, Roles.SOFTWARE_LICENSE]}>
        <DownloadsMenu />
      </RoleRestricted>

      <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.ACCESS_REPORTS_WIZARD_ADMIN, Roles.PERFORMANCE_MONITOR, Roles.QUEST_INTERNAL_REPORTS]}>
        <AdminMenu />
      </RoleRestricted>

      <RoleRestricted allowedRoles={[Roles.VIEWER, Roles.QUEST_USER, Roles.QUEST_ADMIN]}>
        <ToolsMenu />
      </RoleRestricted>

      <HelpMenu />
    </>
  );
};

export default withRouter(Menu);
